<template>
  <nav
    class="fixed inset-x-0 top-0 z-top"
    :data-nav-white="navDropdownIsOpen || thresholdCrossed || navIsWhite"
    @mouseenter="navEntered"
    @mouseleave="navLeft"
    @focusout="navFocusLeft"
  >
    <Container
      class="flex items-start justify-between py-6 transition-all duration-150 ease-linear nav:items-center"
      :class="[
        { 'bg-white': navDropdownIsOpen || thresholdCrossed || navIsWhite },
        { '!py-3': thresholdCrossed },
      ]"
    >
      <NuxtLink
        :to="localePath('/')"
        class="relative z-top -m-2 rounded-sm p-2 ring-offset-0"
        :aria-label="$t('accessibility.link_to_homepage')"
      >
        <LogoIcon
          :class="[
            'h-12 origin-top-left pb-1 text-white transition-transform duration-300 sm:h-16',
            { 'scale-90': thresholdCrossed || mobileMenuIsOpen },
            {
              '!text-black':
                navDropdownIsOpen ||
                thresholdCrossed ||
                navIsWhite ||
                mobileMenuIsOpen ||
                blackLogo,
            },
          ]"
        />
      </NuxtLink>

      <!-- Meta Navigation -->
      <div class="meta hidden flex-col items-end nav:flex">
        <ul
          class="transition-custom -mr-1 flex h-9 space-x-6 overflow-hidden px-1 duration-150 ease-in"
          :class="{ '!h-0 opacity-0': thresholdCrossed && !childHasFocus }"
        >
          <li v-for="{ page } in metaLinks" :key="page.id">
            <NuxtLink
              :to="page.url"
              :target="$useTarget(page.url)"
              class="text-sm uppercase tracking-wide text-white/[0.65] ring-offset-0 transition-colors hover:!text-blue-800 nav-white:text-blue-dark-900/80"
              @focus="childHasFocus = true"
              @blur="childHasFocus = false"
            >
              {{ page.title }}
            </NuxtLink>
          </li>
          <ClientOnly>
            <LangSwitcher
              key="lang-switcher"
              class="mt-[0.125rem] text-white/[0.65] nav-white:text-blue-dark-900/80"
            />
          </ClientOnly>
        </ul>

        <!-- Desktop Navigation -->
        <ul class="nav-items flex items-center text-md font-medium">
          <li
            v-for="(item, index) in mainLinks"
            :key="index"
            @mouseenter="navItemEntered(index, item)"
            @mouseleave="navItemLeft"
          >
            <NavDropdown
              v-if="item.page.blueprint === 'section'"
              :data="item"
              :is-open="openIndex === index"
              :threshold-crossed="thresholdCrossed"
              @toggle="toggleDropdown(index)"
              @close="closeDropdown"
              @closed="dropdownClosed"
            />
            <NavItem v-else :data="item" />
          </li>
          <li class="inline-block px-4">
            <NuxtLink
              class="-m-1 block p-1 ring-offset-0"
              :to="localePath('/search')"
              :aria-label="$t('accessibility.link_to_global_page_search')"
            >
              <SearchIcon
                class="w-4 text-white transition-colors duration-150 hover:text-blue-800 nav-white:text-black"
              />
            </NuxtLink>
          </li>
          <li class="inline-block pl-4">
            <NuxtLink
              class="-m-1 block p-1 ring-offset-0"
              :to="localePath('/login')"
              :aria-label="$t('accessibility.link_to_login_page_for_members')"
            >
              <UserIcon
                class="w-5 text-white transition-colors duration-150 hover:text-blue-800 nav-white:text-black"
              />
            </NuxtLink>
          </li>
        </ul>
      </div>

      <div class="flex items-center nav:hidden">
        <NuxtLink :to="localePath('/login')" class="flex text-md">
          <UserIcon
            class="mr-5 mt-1.5 w-6 text-white sm:mt-2 sm:w-8"
            :class="{
              '!text-blue-800':
                thresholdCrossed || navDropdownIsOpen || navIsWhite,
            }"
          />
        </NuxtLink>

        <!-- Mobile Navigation Button -->
        <MobileMenuButton
          :threshold-crossed="
            thresholdCrossed || navDropdownIsOpen || navIsWhite
          "
        />
      </div>

      <!-- Mobile Navigation Menu -->
      <Transition name="fade">
        <MobileMenu
          v-show="mobileMenuIsOpen"
          :nav-items="mainLinks"
          :meta="metaLinks"
          class="nav:hidden"
        />
      </Transition>
    </Container>
  </nav>
</template>

<script>
import { storeToRefs } from 'pinia'
import { useNavigationStore } from '~/stores/navigation'
import { useGlobalsStore } from '~/stores/globals'

// grace period before the dropdown closes after the pointer left the navigation
const CLOSE_DELAY = 400
// the pointer has to rest on another main item this long before switching,
// so moving diagonally across the navigation doesn't open a neighbour
const SWITCH_DELAY = 150

export default {
  setup() {
    const { isWhite, blackLogo, mobileMenuIsOpen } = storeToRefs(
      useNavigationStore()
    )
    const { mainLinks, metaLinks } = storeToRefs(useGlobalsStore())

    return {
      navIsWhite: isWhite,
      mobileMenuIsOpen,
      mainLinks,
      metaLinks,
      blackLogo,
    }
  },

  data() {
    return {
      thresholdCrossed: false,
      showBtn: false,
      childHasFocus: false,
      openIndex: null,
      // dropdown that opens as soon as the previous one has faded out
      nextIndex: null,
      pointerInside: false,
      closeTimer: null,
      switchTimer: null,
    }
  },

  computed: {
    // also true while switching, so the navigation stays white in between
    navDropdownIsOpen() {
      return this.activeIndex !== null
    },

    activeIndex() {
      return this.nextIndex ?? this.openIndex
    },
  },

  watch: {
    $route() {
      this.closeDropdown()
    },
  },

  mounted() {
    this.registerScrollEvent()
  },

  beforeUnmount() {
    this.clearTimers()
  },

  methods: {
    navItemEntered(index, item) {
      clearTimeout(this.switchTimer)

      const hasDropdown =
        item.page.blueprint === 'section' && item.children.length > 0
      const targetIndex = hasDropdown ? index : null

      if (targetIndex === this.activeIndex) {
        return
      }

      if (!this.navDropdownIsOpen) {
        this.openDropdown(targetIndex)
        return
      }

      this.switchTimer = setTimeout(() => {
        if (targetIndex === null) {
          this.closeDropdown()
        } else {
          this.openDropdown(targetIndex)
        }
      }, SWITCH_DELAY)
    },

    navItemLeft() {
      clearTimeout(this.switchTimer)
    },

    navEntered() {
      this.pointerInside = true
      clearTimeout(this.closeTimer)
    },

    navLeft() {
      this.pointerInside = false
      clearTimeout(this.switchTimer)

      if (this.navDropdownIsOpen) {
        this.closeTimer = setTimeout(this.closeDropdown, CLOSE_DELAY)
      }
    },

    navFocusLeft(event) {
      // clicking on a non-focusable area inside the dropdown also blurs,
      // so only close when the pointer isn't in the navigation either
      if (this.pointerInside || this.$el.contains(event.relatedTarget)) {
        return
      }

      this.closeDropdown()
    },

    toggleDropdown(index) {
      if (this.activeIndex === index) {
        this.closeDropdown()
      } else {
        this.openDropdown(index)
      }
    },

    openDropdown(index) {
      this.clearTimers()

      // let the open dropdown fade out first, the next one fades in afterwards
      if (this.openIndex !== null && this.openIndex !== index) {
        this.nextIndex = index
        this.openIndex = null
        return
      }

      this.nextIndex = null
      this.openIndex = index
    },

    dropdownClosed() {
      if (this.nextIndex === null) {
        return
      }

      this.openIndex = this.nextIndex
      this.nextIndex = null
    },

    closeDropdown() {
      this.clearTimers()
      this.openIndex = null
      this.nextIndex = null
    },

    clearTimers() {
      clearTimeout(this.closeTimer)
      clearTimeout(this.switchTimer)
    },

    registerScrollEvent() {
      const { y } = useScroll(window)

      watchEffect(() => {
        this.thresholdCrossed = y.value > 100
      })
    },
  },
}
</script>

<style scoped lang="scss">
.meta {
  .router-link-exact-active {
    @apply text-blue-800;
  }
}

.nav-items {
  a {
    @apply transition-colors duration-150;
  }

  a:hover {
    @apply text-blue-800;
  }
}

.transition-custom {
  transition-property: height, opacity;
}
</style>
